export type { ZwGraphLayoutFile, ZwGraphNodePos } from "../generated/zwGraph";
export type { ZwGraphNodePos as GraphNodePosition } from "../generated/zwGraph";

import type { ZwGraphNodePos } from "../generated/zwGraph";
import { invoke } from "../invoke";
import {
  entityFrontmatterToWire,
  normalizeEntityFrontmatter,
  type EntityFrontmatter,
} from "../entityFrontmatter";
import {
  normalizePayload,
  type EditorPayload,
  type RawEditorPayload,
} from "./core";

export async function listProjectDatabase(rootPath: string): Promise<string[]> {
  return invoke<string[]>("list_project_database", { rootPath });
}

import type { DatabaseEntrySummary } from "../outlineEntityTypes";

export type { DatabaseEntrySummary };

/** Batch read of every `Database/` entry's typed fields (#72) — used by the
 * outline's type/tag filters. */
export async function listProjectDatabaseEntries(
  rootPath: string,
): Promise<DatabaseEntrySummary[]> {
  const raw = await invoke<{ name: string; isMap: boolean; fields: unknown }[]>(
    "list_project_database_entries",
    { rootPath },
  );
  return raw.map((entry) => ({
    name: entry.name,
    isMap: entry.isMap,
    fields: normalizeEntityFrontmatter(entry.fields),
  }));
}

/** Reads a single entry's typed fields fresh from disk — call whenever the
 * active Database entry changes to populate the fields editor panel. */
export async function getProjectDatabaseEntryFields(
  rootPath: string,
  name: string,
): Promise<EntityFrontmatter> {
  const raw = await invoke<unknown>("get_project_database_entry_fields", {
    rootPath,
    name,
  });
  return normalizeEntityFrontmatter(raw);
}

/** Writes a single entry's typed fields, preserving its body untouched.
 * Independent of the active-buffer autosave — safe to call whether or not
 * `name` is the currently open file. */
export async function updateProjectDatabaseEntryFields(
  rootPath: string,
  name: string,
  fields: EntityFrontmatter,
): Promise<EntityFrontmatter> {
  const raw = await invoke<unknown>("update_project_database_entry_fields", {
    rootPath,
    name,
    fields: entityFrontmatterToWire(fields),
  });
  return normalizeEntityFrontmatter(raw);
}

/** Reads the worldbuilding graph canvas layout from `Database/.zwgraph`. */
export async function getProjectGraphLayout(
  rootPath: string,
): Promise<Record<string, ZwGraphNodePos>> {
  return invoke<Record<string, ZwGraphNodePos>>("get_project_graph_layout", {
    rootPath,
  });
}

/** Writes the worldbuilding graph canvas layout to `Database/.zwgraph`. */
export async function setProjectGraphLayout(
  rootPath: string,
  layout: Record<string, ZwGraphNodePos>,
): Promise<void> {
  await invoke("set_project_graph_layout", { rootPath, layout });
}

export async function createProjectDatabaseEntry(
  rootPath: string,
  name: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("create_project_database_entry", {
    rootPath,
    name,
  });
  return normalizePayload(raw);
}

export async function createProjectDatabaseMap(
  rootPath: string,
  name: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("create_project_database_map", {
    rootPath,
    name,
  });
  return normalizePayload(raw);
}

export async function openProjectDatabaseEntry(
  rootPath: string,
  name: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("open_project_database_entry", {
    rootPath,
    name,
  });
  return normalizePayload(raw);
}

export async function renameProjectDatabaseEntry(
  rootPath: string,
  fromName: string,
  toName: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("rename_project_database_entry", {
    rootPath,
    fromName,
    toName,
  });
  return normalizePayload(raw);
}

export async function deleteProjectDatabaseEntry(
  rootPath: string,
  name: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("delete_project_database_entry", {
    rootPath,
    name,
  });
  return normalizePayload(raw);
}
